import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = ["technique", "match", "training", "progress_before", "progress_after", "other"];

/**
 * VideosSection — reusable videos UI.
 *  - mode="admin": can upload, edit, delete
 *  - mode="parent": read-only + compare + download
 * playerId is required for both.
 * apiPrefix: "/admin/players/{id}" or "/p/{token}"
 * fileUrlFor(videoId): returns the URL to stream that video
 */
export default function VideosSection({ mode, playerId, apiPrefix, fileUrlFor, authHeaders = {} }) {
  const { t } = useLang();
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]); // [videoId, videoId]
  const [blobUrls, setBlobUrls] = useState({}); // videoId -> blobUrl
  const syncBoxRef = useRef(null);
  const videoRefs = useRef({});

  const load = async () => {
    try {
      const r = await axios.get(`${API}${apiPrefix}/videos`, { headers: authHeaders });
      setVideos(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error loading");
    }
  };

  useEffect(() => {
    load();
    return () => Object.values(blobUrls).forEach((u) => URL.revokeObjectURL(u));
    // eslint-disable-next-line
  }, [playerId]);

  const loadBlob = async (videoId) => {
    if (blobUrls[videoId]) return blobUrls[videoId];
    const url = fileUrlFor(videoId);
    const r = await axios.get(url, { headers: authHeaders, responseType: "blob" });
    const bUrl = URL.createObjectURL(r.data);
    setBlobUrls((m) => ({ ...m, [videoId]: bUrl }));
    return bUrl;
  };

  const upload = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const file = fd.get("file");
    if (!file || file.size === 0) return;
    if (file.size > 500 * 1024 * 1024) {
      setError(t("file_too_large"));
      return;
    }
    setBusy(true);
    setError("");
    setProgress(0);
    try {
      await axios.post(`${API}${apiPrefix}/videos`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      e.target.reset();
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload error");
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    await axios.delete(`${API}/admin/videos/${id}`, { headers: authHeaders });
    setVideos((arr) => arr.filter((v) => v.id !== id));
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length >= 2) return [s[1], id];
      return [...s, id];
    });
  };

  const onSyncToggle = (playing) => {
    Object.values(videoRefs.current).forEach((v) => {
      if (!v) return;
      if (playing) v.play().catch(() => {}); else v.pause();
    });
  };

  // Enter comparison view
  if (compareMode && selected.length === 2) {
    const [aId, bId] = selected;
    const a = videos.find((v) => v.id === aId);
    const b = videos.find((v) => v.id === bId);
    return (
      <div className="space-y-3" data-testid="video-compare">
        <div className="flex justify-between items-center gap-3 flex-wrap">
          <h3 className="font-bebas text-2xl tracking-widest text-white">
            🎬 {t("compare_side_by_side")}
          </h3>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-neutral-300">
              <input
                type="checkbox"
                ref={syncBoxRef}
                onChange={(e) => onSyncToggle(e.target.checked)}
                className="w-4 h-4 accent-[#e8559a]"
              />
              {t("sync_play")}
            </label>
            <button
              onClick={() => { setCompareMode(false); setSelected([]); }}
              className="px-3 py-1.5 rounded-md bg-[#1f1f1f] border border-[#333] hover:border-[#e8559a]/50 text-neutral-200 text-sm transition-colors"
            >
              ← {t("exit_compare")}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {[a, b].map((v) => (
            <VideoPlayer key={v.id} video={v} loadBlob={loadBlob} blobUrl={blobUrls[v.id]} refFn={(el) => (videoRefs.current[v.id] = el)} t={t} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="videos-section">
      {mode === "admin" && (
        <form onSubmit={upload} className="rounded-2xl bg-[#161616] border border-[#252525] p-4 sm:p-5">
          <h3 className="font-bebas text-xl tracking-widest text-white mb-3">
            ⬆ {t("upload_video")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input name="title" required placeholder={t("video_title")} className="bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none" />
            <select name="category" defaultValue="technique" className="bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none">
              {CATS.map((c) => <option key={c} value={c}>{t(`cat_${c}`)}</option>)}
            </select>
            <input name="session_date" type="date" className="bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none" />
            <input name="tags" placeholder={t("video_tags")} className="bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none" />
            <input name="description" placeholder={t("video_description")} className="sm:col-span-2 bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none" />
            <input name="file" type="file" accept="video/mp4,video/quicktime,video/webm" required data-testid="video-file-input" className="sm:col-span-2 text-neutral-300 text-sm file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-[#e8559a] file:text-white file:font-medium" />
          </div>
          {busy && (
            <div className="mt-3">
              <div className="text-xs text-neutral-400 mb-1">{t("uploading")} {progress}%</div>
              <div className="h-2 bg-[#0f0f0f] rounded-full overflow-hidden">
                <div className="h-full bg-[#e8559a] transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
          {error && <div className="mt-3 p-2.5 rounded-lg bg-[#e05555]/10 border border-[#e05555]/40 text-[#e05555] text-sm">{error}</div>}
          <div className="mt-3 flex justify-end">
            <button type="submit" disabled={busy} data-testid="video-upload-btn" className="px-5 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] disabled:opacity-60 text-white font-bebas tracking-widest text-sm transition-colors">
              {busy ? "..." : `⬆ ${t("upload_video")}`}
            </button>
          </div>
        </form>
      )}

      <div className="flex items-center justify-between">
        <h3 className="font-bebas text-2xl tracking-widest text-white">🎬 {videos.length} {t("videos")}</h3>
        {videos.length >= 2 && (
          <button
            onClick={() => { setCompareMode(true); }}
            disabled={selected.length !== 2}
            data-testid="compare-btn"
            className="px-3 py-1.5 rounded-md bg-[#e8559a]/10 border border-[#e8559a]/30 disabled:opacity-40 text-[#e8559a] text-xs hover:bg-[#e8559a]/20 transition-colors"
          >
            🎥 {t("compare_side_by_side")} ({selected.length}/2)
          </button>
        )}
      </div>

      {videos.length === 0 ? (
        <div className="rounded-2xl bg-[#161616] border border-[#252525] p-10 text-center text-neutral-500">{t("no_videos")}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {videos.map((v) => {
            const isSel = selected.includes(v.id);
            return (
              <div key={v.id} data-testid={`video-card-${v.id}`} className={`rounded-xl border p-3 transition-all ${isSel ? "bg-[#e8559a]/[0.08] border-[#e8559a]" : "bg-[#161616] border-[#252525]"}`}>
                <VideoPlayer video={v} loadBlob={loadBlob} blobUrl={blobUrls[v.id]} t={t} />
                <div className="mt-2 flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white font-medium truncate">{v.title}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#e8559a]/80 mt-0.5">
                      {t(`cat_${v.category}`)} {v.session_date && `· ${v.session_date}`}
                    </div>
                    {v.description && <div className="text-xs text-neutral-500 mt-1 line-clamp-2">{v.description}</div>}
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <button
                    onClick={() => toggleSelect(v.id)}
                    data-testid={`select-${v.id}`}
                    className={`px-2 py-1 rounded text-[10px] uppercase tracking-widest transition-colors ${isSel ? "bg-[#e8559a] text-white" : "bg-[#0f0f0f] border border-[#252525] text-neutral-400 hover:border-[#e8559a]/40"}`}
                  >
                    {isSel ? "✓ Selected" : "Select"}
                  </button>
                  {mode === "admin" && (
                    <button
                      onClick={() => remove(v.id)}
                      data-testid={`del-${v.id}`}
                      className="px-2 py-1 rounded text-[10px] bg-[#e05555]/10 border border-[#e05555]/30 text-[#e05555] hover:bg-[#e05555]/20 transition-colors"
                    >
                      🗑
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VideoPlayer({ video, loadBlob, blobUrl, refFn, t }) {
  const [loading, setLoading] = useState(false);
  const load = async () => {
    if (blobUrl) return;
    setLoading(true);
    try { await loadBlob(video.id); } finally { setLoading(false); }
  };

  if (!blobUrl) {
    return (
      <button
        type="button"
        onClick={load}
        className="w-full aspect-video rounded-lg bg-[#0a0a0a] border border-[#252525] flex items-center justify-center text-neutral-400 hover:border-[#e8559a]/50 transition-colors"
      >
        {loading ? "..." : `▶ Load video`}
      </button>
    );
  }
  return (
    <video
      ref={refFn}
      src={blobUrl}
      controls
      preload="metadata"
      className="w-full aspect-video rounded-lg bg-black"
    >
      Your browser does not support video.
    </video>
  );
}
